import React from 'react';
import {
  Card, CardContent, CardMedia, Button, IconButton, Tooltip, Chip,
  Typography, Dialog, DialogActions, DialogContent, DialogTitle, Box, Stack, Autocomplete, TextField
} from '@mui/material';
import {
  Delete, Settings, Block, Check, Public, Person, Timeline, Add, QrCode, Flag, PlayCircleOutline
} from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import * as ETVConstants from '../ETVConstants';
import CropOriginalIcon from '@mui/icons-material/CropOriginal';
import QRCodeOverlay from '../tools/QRCodeOverlay.js';
import TimeDateSettings from '../tools/TimeDateSettings.js';
import RestrictionSettings from '../tools/RestrictionSettings.js';
import ETVPopup from '../tools/ETVPopup';
import ETVActionButton from '../tools/ETVActionButton';
import { stripNumericPrefix } from './ContentLibraryUtils';

class ContentLibraryItem extends React.Component {
  state = {
    window: '',
    new_owner_id: -1,
    qr_code_url: '',
    qr_code_alignmnet:0,
    user_name: ''
  };

  componentDidMount() {
    this.setState({
      new_owner_id: this.props.user_id,
      user_name: localStorage.getItem('etv.user_name') || ''
    });
  }

  // ---------- helpers ----------
  isMovie = (f = '') => /\.mp(g|4|eg)$/i.test(f);
  isHTML  = (f = '') => /\.html?$/i.test(f);
  isMusic = (f = '') => /\.(mp3|m4a)$/i.test(f);
  isPDF   = (f = '') => /\.pdf$/i.test(f);

  makeImageUrlRelative(url) {
    if (!url) return url;
    const idx = url.indexOf('/contents');
    return idx >= 0 ? url.substring(idx) : url;
  }

  getThumbnail() {
    const title = (this.props.item.filename || '').toLowerCase();
    let imageUrl = this.props.item.thumbnail;
    if (ETVConstants.ON_PREMISE_VERSION || !imageUrl) {
      if (this.isMovie(title)) imageUrl = '/image_assets/content/movie_placeholder.png';
      else if (this.isHTML(title))  imageUrl = '/image_assets/content/html_placeholder.png';
      else if (this.isMusic(title)) imageUrl = '/image_assets/content/music_placeholder.png';
      else if (this.isPDF(title))   imageUrl = '/image_assets/content/pdf_placeholder.png';
    }
    return imageUrl;
  }

  getChipColor(category) {
    if (category === 'image') return '#199139';
    if (category === 'movie') return '#1d10a3';
    if (category === 'pdf')   return '#fc2116';
    if (category === 'html')  return '#c28f04';
    return '#e82764';
  }

  ownerIsChild = (ownerID) => (this.props.children || []).includes(ownerID); // NOTE: consider renaming this prop to avoid confusion with React children
  userCanContributeMarketplace = () => (this.props.user_roles || []).includes('admin.content.contribute_marketplace');

  // ---------- actions ----------
  addToChannel = async (contentID, channel, deviceID, groupID) => {
    let url = `${ETVConstants.getServerBase()}/playlist?action=add_to_playlist&channel=${channel}&device_id=${deviceID}&user_id=${this.props.user_id}&content_id=${contentID}`;
    if (groupID >= 0) {
      url = `${ETVConstants.getServerBase()}/playlist?action=add_to_group_playlist&channel=${channel}&group_id=${groupID}&user_id=${this.props.user_id}&content_id=${contentID}`;
    }
    try {
      const result = await ETVConstants.sendCMDToServerAwait(url);
      if (!result || result.result !== false) {
        this.props.addedToChannelF();
      }
    } catch (error) {
      console.error("Could not add content to playlist", error);
    }
  };

  showWindow = (id, show) => this.setState({ window: show ? id : '' });
  showChangeOwner       = () => this.showWindow('show_change_owner_div', true);
  showQRCode            = () => this.showWindow('qr_code_div', true);
  showRestrictions      = () => this.showWindow('restrictions_div', true);
  showTimeDateSettings  = () => this.showWindow('time_date_div', true);

  saveNewOwner = () => {
    const url = `${ETVConstants.getServerBase()}/content_library?action=update_owner&user_id=${this.state.new_owner_id}&content_id=${this.props.id}`;
    ETVConstants.sendCMDToServerListener(url, () => this.showWindow('show_change_owner_div', false));
  };

  updateQRCode = (qr, alignment) => this.setState({ qr_code_url: qr, qr_code_alignment:alignment });

  saveQRCode = () => {
    const url = `${ETVConstants.getServerBase()}/content_library?action=set_qr_code&content_id=${this.props.id}&qr_code_alignment=${this.state.qr_code_alignment}&qr_code=${encodeURIComponent(this.state.qr_code_url)}`;
    //console.log("---->updateQR: "+url);
    ETVConstants.sendCMDToServerListener(url, () => this.showWindow('qr_code_div', false));
  };

  deleteQRCode = () => {
    const url = `${ETVConstants.getServerBase()}/content_library?action=set_qr_code&content_id=${this.props.id}&qr_code=`;
    ETVConstants.sendCMDToServerListener(url, () => this.showWindow('qr_code_div', false));
  };

  isValidQRUrl(content) {
    if (typeof content !== 'string' || content.trim().length === 0) return false;
    if (content.startsWith('BEGIN:VCARD') || content.startsWith('WIFI:') || content.startsWith('MATMSG:')) return true;
    try {
      const parsed = new URL(content);
      return ['http:', 'https:', 'sms:', 'tel:'].includes(parsed.protocol);
    } catch {
      return false;
    }
  }

  getAvailableUserList() {
    const { available_users, user_id } = this.props;
    const { new_owner_id, user_name } = this.state;

    if (!available_users || available_users.length === 0) return null;

    // Merge current user + available users (avoid duplicates)
    const options = [
      { user_id, user_name },
      ...available_users.filter(u => u.user_id !== user_id),
    ];

    const selectedUser =
      options.find(u => u.user_id === new_owner_id) || null;

    return (
      <Box mt={2}>
        <Autocomplete
          size="small"
          options={options}
          value={selectedUser}
          getOptionLabel={(option) => option.user_name || ''}
          isOptionEqualToValue={(option, value) =>
            option.user_id === value.user_id
          }
          onChange={(event, newValue) => {
            this.setState({
              new_owner_id: newValue ? newValue.user_id : null,
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={ETVConstants.trans('change_owner')}
              variant="outlined"
              sx={{ minWidth: 220 }}
            />
          )}
        />
      </Box>
    );
  }

  formatAspectRatio(r) {
    if (!r || typeof r !== 'number' || r=="-1") return '';

    // --- Common well-known ratios ---
    const known = [
      { ratio: 16/9,  label: '16:9' },
      { ratio: 9/16,  label: '9:16' },
      { ratio: 4/3,   label: '4:3' },
      { ratio: 3/4,   label: '3:4' },
      { ratio: 5/4,  label: '5:4' },
      { ratio: 4/5,  label: '4:5' },
      { ratio: 1,     label: '1:1' },
      { ratio: 21/9,  label: '21:9' },
      { ratio: 9/21,  label: '9:21' }
    ];

    for (const k of known) {
      if (Math.abs(r - k.ratio) < 0.01) return k.label;   // tolerance
    }

    const precision = 100; // increase if you want more accuracy
    let numerator   = Math.round(r * precision);
    let denominator = precision;

    const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
    const d = gcd(numerator, denominator);

    numerator   /= d;
    denominator /= d;

    return `${numerator}:${denominator}`;
  }




  render() {
    // --- Uniform card & thumbnail ---
    const CARD_WIDTH   = 220;
    const CARD_HEIGHT  = 340;        // keeps grid rows aligned
    const THUMB_RATIO  = 4 / 3;      // change to 16/9 if you prefer
    const thumbPad     = `${100 / THUMB_RATIO}%`; // 75% for 4:3

    // Data
    const rawTitle = this.props.item.filename || '';
    const title    = stripNumericPrefix(rawTitle);
    const rawDescription = this.props.item.description || '';
    const cleanDescription = stripNumericPrefix(rawDescription);
    const desc = cleanDescription === title || (cleanDescription !== rawDescription && /\.[a-z0-9]{1,8}$/i.test(rawDescription)) ? cleanDescription : rawDescription;
    const active   = !!this.props.item.active;
    const isPublic = !!this.props.item.public;
    const canEdit  = this.props.item.owner_id === this.props.user_id || this.ownerIsChild(this.props.item.owner_id);
    const category = this.props.item.content_category || '';
    const contentRatio = this.formatAspectRatio(this.props.item.content_ratio);

    const imgSrc      = this.getThumbnail();
    const resolvedImg = ETVConstants.ON_PREMISE_VERSION ? this.makeImageUrlRelative(imgSrc) : imgSrc;

    const qrOpen   = this.state.window === 'qr_code_div';
    const ownOpen  = this.state.window === 'show_change_owner_div';
    const resOpen  = this.state.window === 'restrictions_div';
    const timeOpen = this.state.window === 'time_date_div';
	const previewOpen = this.state.window === 'movie_preview_div';
	const movie = this.isMovie(rawTitle);
	const movieUrl = ETVConstants.ON_PREMISE_VERSION ? this.makeImageUrlRelative(this.props.item.url) : this.props.item.url;

    return (
      <Card
        sx={{
          width: CARD_WIDTH,
          height: this.props.marketplace ? 'auto' : CARD_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          boxShadow: '0 5px 16px rgba(15,23,42,0.16)',
          transition: 'transform .15s ease, box-shadow .15s ease',
          '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 30px rgba(15,23,42,0.24)' }
        }}
      >
        {/* Thumbnail with fixed aspect ratio */}
        <Box
		  onClick={movie ? () => this.showWindow('movie_preview_div', true) : undefined}
		  role={movie ? 'button' : undefined}
		  tabIndex={movie ? 0 : undefined}
		  onKeyDown={movie ? (event) => { if(event.key==='Enter' || event.key===' ') this.showWindow('movie_preview_div', true); } : undefined}
		  sx={{position:'relative',width:'100%',pt:thumbPad,bgcolor:'action.hover',cursor:movie?'pointer':'default','&:hover .movie-preview-overlay':{opacity:1}}}>
          <CardMedia
            component="img"
            loading="lazy"
            src={resolvedImg}
            alt={title}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center'
            }}
          />
		  {movie && <Box className="movie-preview-overlay" sx={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',bgcolor:'rgba(0,0,0,0.28)',opacity:0.75,transition:'opacity .15s ease'}}><PlayCircleOutline sx={{fontSize:58,color:'common.white',filter:'drop-shadow(0 2px 5px rgba(0,0,0,.5))'}}/></Box>}
          <Chip
            size="small"
            label={category ? ETVConstants.trans(category) : ETVConstants.trans('uncategorized')}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              color: category ? 'common.white' : 'common.white',
              bgcolor: category ? this.getChipColor(category) : '#696865',
              border: category ? 0 : '1px solid',
              borderColor: 'divider'
            }}
          />
          {!!contentRatio && (
            <Chip
              size="small"
              icon={<CropOriginalIcon sx={{ fontSize: 14 }} />}
              label={contentRatio}
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.72)',
                color: 'common.white',
                height: 22,
                '& .MuiChip-label': {
                  px: 1
                },
                '& .MuiChip-icon': {
                  color: 'inherit',
                  ml: 0.25
                }
              }}
            />
          )}          
        </Box>

        {/* Body */}
        <CardContent sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5, flexGrow: this.props.marketplace ? 0 : 1 }}>
          {!this.props.marketplace && (
            <Typography
              variant="subtitle2"
              sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              title={title}
            >
              {title}
            </Typography>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '2.6em'
            }}
            title={desc}
          >
            {desc}
          </Typography>

          {/* Actions */}
          {this.props.addedToChannelF ? (
            <Box sx={{ mt: this.props.marketplace ? 0.75 : 'auto' }}>
              <Button
                variant="contained"
                size="small"
                color="success"
                startIcon={<Add />}
                onClick={() =>
                  this.addToChannel(this.props.id, this.props.channel, this.props.device_id, this.props.group_id)
                }
                fullWidth
              >
                {ETVConstants.trans('add')}
              </Button>
            </Box>
          ) : (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ mt: 'auto', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}
            >
              {/* Active toggle */}
              {!active ? (
                <Tooltip title={ETVConstants.trans('set_active')}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!canEdit}
                      sx={{ color: '#ff3b30' }}
                      onClick={() => this.props.activeF(this.props.id, !this.props.active)}
                    >
                      <Block />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title={ETVConstants.trans('set_inactive')}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!canEdit}
                      sx={{ color: '#00ad0e' }}
                      onClick={() => this.props.activeF(this.props.id, !this.props.active)}
                    >
                      <Check />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* Marketplace publish toggle */}
              {this.userCanContributeMarketplace() && !ETVConstants.ON_PREMISE_VERSION && (
                isPublic ? (
                  <Tooltip title={ETVConstants.trans('unpublish_from_marketplace')}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!canEdit}
                        color="primary"
                        onClick={() => this.props.publicF(this.props.id, !isPublic)}
                      >
                        <Public />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title={ETVConstants.trans('publish_to_marketplace')}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!canEdit}
                        color="secondary"
                        onClick={() => this.props.publicF(this.props.id, !isPublic)}
                      >
                        <Block />
                      </IconButton>
                    </span>
                  </Tooltip>
                )
              )}

              <Tooltip title={ETVConstants.trans('settings')}>
                <IconButton size="small" color="primary" onClick={() => this.props.showSettingsF(this.props.id)}>
                  <Settings />
                </IconButton>
              </Tooltip>

              <Tooltip title={ETVConstants.trans('hit_report')}>
                <IconButton size="small" color="primary" onClick={() => this.props.showHitReportF(this.props.id)}>
                  <Timeline />
                </IconButton>
              </Tooltip>

              {this.props.available_users && this.props.available_users.length >= 1 && (
                <Tooltip title={ETVConstants.trans('change_owner')}>
                  <IconButton size="small" color="primary" onClick={this.showChangeOwner}>
                    <Person />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={ETVConstants.trans('time_date_settings')}>
                <span>
                  <IconButton size="small" color="primary" onClick={this.showTimeDateSettings}>
                    <AccessTimeIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={ETVConstants.trans('qr_code_overlay')}>
                <span>
                  <IconButton size="small" color="primary" onClick={this.showQRCode}>
                    <QrCode />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={ETVConstants.trans('restrictions')}>
                <span>
                  <IconButton size="small" color="primary" onClick={this.showRestrictions}>
                    <Flag />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={ETVConstants.trans('delete')}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!canEdit}
                    color={canEdit ? 'error' : 'primary'}
                    onClick={() => this.showWindow('confirm_delete_content', true)}
                  >
                    <Delete />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          )}
        </CardContent>

        {/* Dialogs — children gated to avoid rendering undefined components */}
        <ETVPopup
          open={this.state.window === 'confirm_delete_content'}
          onClose={() => this.showWindow('', false)}
          width="small"
          icon={<Delete color="error" />}
          title={ETVConstants.trans('delete')}
          description={ETVConstants.trans('confirm_delete') || 'Please confirm this action.'}
          bottomActions={
            <>
              <ETVActionButton action="cancel" onClick={() => this.showWindow('', false)} />
              <Button
                color="error"
                variant="contained"
                startIcon={<Delete />}
                onClick={() => {
                  this.showWindow('', false);
                  this.props.deleteF(this.props.id);
                }}
              >
                {ETVConstants.trans('delete')}
              </Button>
            </>
          }
        >
          <Typography>{ETVConstants.trans('confirm_delete') || 'Are you sure you want to delete this file?'}</Typography>
          <Typography sx={{ mt: 1 }} fontWeight={700} noWrap title={title}>{title}</Typography>
        </ETVPopup>

        <ETVPopup open={ownOpen} onClose={() => this.showWindow('', false)} width="small"
          icon={<Person/>} title={ETVConstants.trans('change_owner')}
          bottomActions={<><ETVActionButton action="cancel" onClick={() => this.showWindow('', false)}/><ETVActionButton action="save" onClick={this.saveNewOwner}/></>}>
            <Typography variant="body2">{ETVConstants.trans('change_owner_help')}</Typography>
            {this.getAvailableUserList()}
        </ETVPopup>

        <ETVPopup open={qrOpen} onClose={() => this.showWindow('', false)} width="small"
          icon={<QrCode/>} title={ETVConstants.trans("qr_code_overlay")}
          bottomActions={<>
            <ETVActionButton action="cancel" onClick={() => this.showWindow('', false)}/>
            <Button onClick={this.deleteQRCode} disabled={!this.state.qr_code_url} startIcon={<Delete />} color="error" variant="contained">{ETVConstants.trans('delete')}</Button>
            <ETVActionButton action="save" onClick={this.saveQRCode} disabled={!this.state.qr_code_url || !this.isValidQRUrl(this.state.qr_code_url)}/>
          </>}>
            {qrOpen && QRCodeOverlay && (
              this.isPDF(this.props.item.filename)
                ? <QRCodeOverlay disabled={!canEdit} content_id={this.props.item.content_id} download_url={this.props.item.url} onQRUrlChange={this.updateQRCode} />
                : <QRCodeOverlay disabled={!canEdit} content_id={this.props.item.content_id} onQRUrlChange={this.updateQRCode} />
            )}
        </ETVPopup>

        <ETVPopup open={resOpen} onClose={() => this.showWindow('', false)} width="standard"
          icon={<Flag/>} title={ETVConstants.trans('restrictions')}
          bottomActions={<ETVActionButton action="cancel" label={ETVConstants.trans('close')} onClick={() => this.showWindow('', false)}/>}>
            {resOpen && RestrictionSettings && (
              <RestrictionSettings disabled={!canEdit} content_id={this.props.item.content_id} />
            )}
        </ETVPopup>

        <ETVPopup open={timeOpen} onClose={() => this.showWindow('', false)} width="standard"
          icon={<AccessTimeIcon/>} title={ETVConstants.trans('time_date_settings')}
          bottomActions={<ETVActionButton action="cancel" label={ETVConstants.trans('close')} onClick={() => this.showWindow('', false)}/>}>
            {timeOpen && TimeDateSettings && (
              <TimeDateSettings disabled={!canEdit} content_id={this.props.item.content_id} />
            )}
        </ETVPopup>

		<Dialog open={previewOpen} maxWidth="lg" fullWidth onClose={() => this.showWindow('', false)}>
		  <DialogTitle>{title}</DialogTitle>
		  <DialogContent dividers sx={{p:0,bgcolor:'#111',display:'flex',justifyContent:'center'}}>
			{previewOpen && <video src={movieUrl} controls autoPlay style={{display:'block',width:'100%',maxHeight:'75vh',background:'#111'}} />}
		  </DialogContent>
		  <DialogActions><Button onClick={() => this.showWindow('', false)}>{ETVConstants.trans('close')}</Button></DialogActions>
		</Dialog>
      </Card>
    );
  }
}

export default ContentLibraryItem;
